import { useState } from "react";

export default function AppearanceSettingsPanel() {
  const [settings, setSettings] = useState({
    theme: "light",
    primaryColor: "#1B6E4F",
    density: "comfortable",
    fontSize: "medium",
    language: "pt-BR",
    cardStyle: "rounded",
    showTips: true,
    showEstimates: true,
    enableAnimations: true,
    respectReducedMotion: true,
  });

  const handleChange = (field: string, value: string | boolean) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-background-200/70 p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Aparência</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Tema</label>
            <div className="flex gap-2">
              <button onClick={() => handleChange("theme", "light")} className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg border transition-colors whitespace-nowrap ${settings.theme === "light" ? "bg-primary-50 text-primary-700 border-primary-300" : "bg-white text-foreground-600 border-background-200"}`}>
                <i className="ri-sun-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />Claro
              </button>
              <button onClick={() => handleChange("theme", "dark")} className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg border transition-colors whitespace-nowrap ${settings.theme === "dark" ? "bg-primary-50 text-primary-700 border-primary-300" : "bg-white text-foreground-600 border-background-200"}`}>
                <i className="ri-moon-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />Escuro
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Cor principal</label>
            <div className="flex items-center gap-3">
              <input type="color" value={settings.primaryColor} onChange={(e) => handleChange("primaryColor", e.target.value)} className="w-10 h-10 rounded-lg border border-background-200 cursor-pointer" />
              <span className="text-xs text-foreground-500 font-mono">{settings.primaryColor}</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Densidade da interface</label>
            <select value={settings.density} onChange={(e) => handleChange("density", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
              <option value="compact">Compacta</option>
              <option value="comfortable">Confortável</option>
              <option value="spacious">Espaçosa</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Tamanho da fonte</label>
            <select value={settings.fontSize} onChange={(e) => handleChange("fontSize", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
              <option value="small">Pequena</option>
              <option value="medium">Média</option>
              <option value="large">Grande</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Idioma</label>
            <select value={settings.language} onChange={(e) => handleChange("language", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
              <option value="pt-BR">Português (Brasil)</option>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-background-200/70 p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Comportamento</h3>
        <div className="space-y-3">
          <ToggleRow label="Mostrar dicas técnicas" checked={settings.showTips} onChange={() => handleChange("showTips", !settings.showTips)} />
          <ToggleRow label="Mostrar avisos de estimativa preliminar" checked={settings.showEstimates} onChange={() => handleChange("showEstimates", !settings.showEstimates)} />
          <ToggleRow label="Ativar animações" checked={settings.enableAnimations} onChange={() => handleChange("enableAnimations", !settings.enableAnimations)} />
          <ToggleRow label="Respeitar redução de movimento" checked={settings.respectReducedMotion} onChange={() => handleChange("respectReducedMotion", !settings.respectReducedMotion)} />
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white rounded-xl border border-background-200/70 p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Preview visual</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-4 rounded-xl border border-background-200 bg-background-50">
            <div className="w-full h-2 rounded-full bg-primary-200 mb-3"><div className="w-3/4 h-full rounded-full bg-primary-500" /></div>
            <p className="text-xs font-semibold text-foreground-900">Card de exemplo</p>
            <p className="text-xs text-foreground-400 mt-1">Descrição do card com estilo aplicado</p>
          </div>
          <div className="p-4 rounded-xl border border-background-200 bg-background-50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-xs font-bold">A</div>
              <span className="text-xs font-semibold text-foreground-900">Avatar preview</span>
            </div>
            <p className="text-xs text-foreground-400">Elementos de interface com tema aplicado</p>
          </div>
          <div className="p-4 rounded-xl border border-background-200 bg-background-50">
            <div className="flex gap-2 mb-3">
              <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Ativo</span>
              <span className="px-2 py-0.5 rounded-full bg-warning-100 text-warning-700 text-xs font-medium">Alerta</span>
            </div>
            <p className="text-xs text-foreground-400">Badges e indicadores de status</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex items-center justify-between py-2 cursor-pointer">
      <span className="text-sm text-foreground-700">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${checked ? "bg-primary-500" : "bg-foreground-300"}`}
      >
        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
      </button>
    </label>
  );
}